import React from 'react';
import type { DataNode, Key } from 'rc-tree/lib/interface';
import { MonitoringTreeNode, MonitoringDataNode } from './monitoring.types';
import {
  MONITORING_TREE_LEVELS,
  MONITORING_LEVEL_ICONS,
} from './monitoring.consts';

export const getAllSelectedCodeIds = (
  checkedNodes: MonitoringDataNode[],
): number[] => {
  return checkedNodes
    .filter(node => node.isLeaf)
    .map(node => node.codeId)
    .filter((id): id is number => id !== undefined);
};

const apiNodeToTreeNode = (
  node: MonitoringTreeNode,
  parentPath: string[],
  depth: number,
): MonitoringDataNode => {
  const path = [...parentPath, node.name];
  return {
    key: JSON.stringify(path),
    codeId: node.leaf,
    title: node.name,
    isLeaf: node.leaf !== undefined,
    icon: React.createElement(
      MONITORING_LEVEL_ICONS[MONITORING_TREE_LEVELS[depth]],
    ),
    children: node.subTree?.map(child =>
      apiNodeToTreeNode(child, path, depth + 1),
    ),
  };
};

export const normalizeApiNodeToTreeNode = (
  nodes: MonitoringTreeNode[],
): MonitoringDataNode[] => nodes.map(node => apiNodeToTreeNode(node, [], 0));

export const getAllTreeKeys = (nodes: DataNode[]): Key[] =>
  nodes.flatMap(node => [node.key, ...getAllTreeKeys(node.children ?? [])]);

export const filterTreeNodesByCodeIds = (
  nodes: MonitoringDataNode[],
  codeIds: number[],
): MonitoringDataNode[] => {
  const allowed = new Set(codeIds);

  const parseNode = (list: MonitoringDataNode[]): MonitoringDataNode[] =>
    list.reduce<MonitoringDataNode[]>((acc, node) => {
      if (node.isLeaf) {
        if (node.codeId !== undefined && allowed.has(node.codeId)) {
          acc.push(node);
        }
        return acc;
      }
      const children = parseNode(node.children ?? []);
      if (children.length > 0) acc.push({ ...node, children });
      return acc;
    }, []);

  return parseNode(nodes);
};
